import type { ReactNode } from "react";
import { StyleSheet, Text, View } from "react-native";
import { useTranslation } from "react-i18next";

import { Button } from "./Button";
import { IconButton } from "./IconButton";
import type { UiTheme } from "../theme";

// THE top chrome of a NAVIGATED editor surface: ‹ back · title · Save, plus the
// error line that belongs to that Save. Extracted from ComposerEditLayout's
// hand-rolled `saveBar` at CO-7's second real callsite (the coach target editor)
// — extracted, not mirrored: "rispecchia il fratello" is the anti-pattern the
// CN-disclosure-card slice already paid for.
//
// It is NOT the Modal family. PageSheet's Cancel·Title·Action reads identical
// but is a scrim/tap-out sheet; this is a surface you navigated TO, so its back
// is a nav affordance and its dismissal is the host's, not a tap-out.
// It is also NOT mobile's <ScreenHeader> (apps/mobile/src/components): that one
// is the top-level TAB header — no back, self-erasing when empty, spinner
// overlay. Different concept, different lifetime.
//
// 🧱 The planned `<ScreenHeader withBack>` wave (next/consolidation.md §NEXT
// WAVES — the 32/44/56/64 spacer-width fake-centering bug) overlaps this bar's
// back+title half. When it lands it must ABSORB or COMPOSE this, never sit
// beside it as a third header idiom. Registered in consolidation.md.
//
// Why the box internals are the <Button> primitive and not a local StyleSheet:
// the original hand-rolled its filled button (brand500 fill, radius 10, its own
// disabled fill + disabled text colour) — precisely the ~20-silo duplication
// <Button> exists to end. Consequence, accepted by Antenore 2026-07-15 ("fai
// primitiva"): the composer's bar shifts a few px/pt onto the encoded sizes.

export type EditorTopBarProps = {
  theme: UiTheme;
  /** Left nav affordance — the surface you go back TO ("‹ Leonardo"). */
  backLabel: string;
  onBack: () => void;
  /** PE-32 — drop the back LABEL and keep the glyph, buying the width for
   *  `center`. `backLabel` is still required and becomes the spoken name, so
   *  the affordance loses pixels, never its announcement. ⛔ Save keeps its
   *  text label: it is the commit of a surface where an hour of work is at
   *  stake, and Antenore's call (29/08) was that the space is paid by the
   *  back label, not by that one. */
  backIconOnly?: boolean;
  title: string;
  /** PE-32 — a control that lives in the bar's slack instead of the title:
   *  the prescribe editor's writing-language chip. Renders INSTEAD of
   *  `title`, which that surface leaves empty anyway (the name is an editable
   *  field in its scroller). The tray such a chip opens belongs to the
   *  caller, under the bar — this bar stays one row tall. */
  center?: ReactNode;
  onSave: () => void;
  /** Save in flight: the button shows a spinner and refuses presses. Back stays
   *  live on purpose — a wedged save must never trap you on the surface. */
  busy: boolean;
  /** Save is not yet possible (distinct from `busy`): the button greys out
   *  without a spinner. The coach editor uses it while the ETag write-lock is
   *  still loading — a save then would be a self-inflicted 428. */
  saveDisabled?: boolean;
  /** Defaults to the shared "Save" copy; both callsites use it. */
  saveLabel?: string;
  /** Save-path error, rendered under the bar. Null/absent → no line, no gap. */
  error?: string | null;
  /** #336: the save-state line under the bar (InlineStatusHint): where the
   *  edits are now. Above the error line, which is about one failed write. */
  status?: ReactNode;
};

export function EditorTopBar({
  theme,
  backLabel,
  onBack,
  backIconOnly,
  title,
  center,
  onSave,
  busy,
  saveDisabled,
  saveLabel,
  error,
  status,
}: EditorTopBarProps) {
  const { t } = useTranslation();
  const styles = makeStyles(theme);

  return (
    <>
      <View style={styles.bar}>
        {backIconOnly ? (
          <IconButton
            theme={theme}
            name="chevron-left"
            accessibilityLabel={backLabel}
            onPress={onBack}
          />
        ) : (
          <Button
            theme={theme}
            variant="ghost"
            icon="chevron-left"
            label={backLabel}
            onPress={onBack}
          />
        )}
        {/* flex:1 between two auto-width buttons: the title takes the slack and
            truncates, so a long program name can never shove Save off-screen.
            ⛔ An EMPTY title is a real case, not a bug to defend against: the
            prescribe editor's name is an editable FIELD in its scroll body, and
            a bar that also announced it would say it twice — so the slack is
            taken by a plain spacer and a screen reader finds one heading, or
            none. (PE-31b: those rows used to live here, in a `meta` slot; they
            moved into the page's own scroller when the fixed header proved too
            tall for the card underneath it, and the slot went with them.) */}
        {center ? (
          <View style={styles.center}>{center}</View>
        ) : title ? (
          <Text style={styles.title} numberOfLines={1} accessibilityRole="header">
            {title}
          </Text>
        ) : (
          <View style={styles.title} />
        )}
        <Button
          theme={theme}
          variant="primary"
          label={saveLabel ?? t("program.save", { defaultValue: "Save" })}
          loading={busy}
          disabled={saveDisabled}
          onPress={onSave}
        />
      </View>
      {status ? <View style={styles.status}>{status}</View> : null}
      {error ? (
        <Text style={styles.error} accessibilityLiveRegion="polite">
          {error}
        </Text>
      ) : null}
    </>
  );
}

function makeStyles(theme: UiTheme) {
  const { palette: p, spacing: s, typography: ty } = theme;
  return StyleSheet.create({
    bar: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      paddingHorizontal: s.s5,
      paddingVertical: s.s4,
      borderBottomWidth: 1,
      borderBottomColor: p.border,
      gap: s.s4,
    },
    title: {
      flex: 1,
      color: p.ink,
      fontSize: ty.sizes.lg,
      fontWeight: "700",
    },
    // Same slack the title takes, so a long endonym truncates inside the bar
    // instead of shoving Save off-screen; left-aligned like the title it
    // replaces, not centred, so back and chip read as one group.
    center: { flex: 1, flexDirection: "row", alignItems: "center" },
    status: {
      paddingHorizontal: s.s5,
      paddingVertical: s.s2,
      borderBottomWidth: 1,
      borderBottomColor: p.border,
    },
    error: {
      color: p.danger,
      fontSize: ty.sizes.base,
      paddingHorizontal: s.s5,
      paddingTop: s.s3,
    },
  });
}
